import { PageHeader } from "@/components/layout/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingState } from "@/components/ui/loading-state";
import { TruncatedText } from "@/components/ui/truncation-tooltip";
import { addUnitSynonym, checkWorkOrder, getFiles } from "@/services/api";
import { uiMessages } from "@/services/messages";
import { type CheckWorkOrderResponse, type FileResponse, formatFileSheetLabel } from "@efes/shared";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, MoreHorizontal, Plus, XCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

// Раздел «Проверка нарядов» работает только с нарядами. Акты и сметы
// убраны из списка — для актов есть собственный workflow в Сверке.
const allowedTypes = new Set<FileResponse["fileType"]>(["work_order"]);

const fileTypeLabels: Record<FileResponse["fileType"], string> = {
	work_order: "Наряд",
	completion_act: "Акт",
	estimate: "Смета",
	rate_book: "Книга расценок",
	unclassified: "Не определён",
};

function formatPeriod(month: number | null, year: number | null): string {
	if (!month && !year) return "—";
	const m = month ? String(month).padStart(2, "0") : "??";
	const y = year ?? "????";
	return `${m}/${y}`;
}

function formatUploadDate(value: string): string {
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function formatDecimal(value: number, maxFractionDigits = 3): string {
	return value.toLocaleString("ru-RU", {
		minimumFractionDigits: 0,
		maximumFractionDigits: maxFractionDigits,
	});
}

function formatInteger(value: number): string {
	return value.toLocaleString("ru-RU");
}

interface OpenMenuState {
	fileId: number;
	left: number;
	top: number;
}

type LineFilter = "all" | "passed" | "error" | "warning";

export function VerificationPage() {
	const [openMenu, setOpenMenu] = useState<OpenMenuState | null>(null);
	const [activeDocumentId, setActiveDocumentId] = useState<number | null>(null);
	const [checkResults, setCheckResults] = useState<Record<number, CheckWorkOrderResponse>>({});
	const [checkLoading, setCheckLoading] = useState<number | null>(null);
	const [checkError, setCheckError] = useState<string | null>(null);
	const [activeLineFilter, setActiveLineFilter] = useState<LineFilter | null>(null);

	const { data, isLoading } = useQuery({
		// Тот же ключ, что у страницы загрузки: общий кэш и инвалидация.
		queryKey: ["files"],
		queryFn: () => getFiles(),
	});

	const files = useMemo(() => {
		const allFiles = data?.files ?? [];
		return allFiles
			.filter((file) => allowedTypes.has(file.fileType))
			.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
	}, [data?.files]);

	const activeDocument = useMemo(
		() => files.find((file) => file.id === activeDocumentId) ?? null,
		[files, activeDocumentId],
	);
	const activeResult = activeDocument ? (checkResults[activeDocument.id] ?? null) : null;
	const filteredLines = useMemo(() => {
		if (!activeResult) return [];

		switch (activeLineFilter) {
			case "all":
				return activeResult.lines;
			case "passed":
				return activeResult.lines.filter((line) => line.passed);
			case "error":
				return activeResult.lines.filter((line) => lineHasError(line));
			case "warning":
				return activeResult.lines.filter(
					(line) =>
						!lineHasError(line) &&
						line.violations.some((violation) => violation.severity === "warning"),
				);
			default:
				return activeResult.lines;
		}
	}, [activeResult, activeLineFilter]);

	useEffect(() => {
		const onDocumentMouseDown = (event: MouseEvent) => {
			const target = event.target as HTMLElement | null;
			if (target?.closest("[data-row-menu-root='true']")) return;
			setOpenMenu(null);
		};

		document.addEventListener("mousedown", onDocumentMouseDown);
		return () => document.removeEventListener("mousedown", onDocumentMouseDown);
	}, []);

	useEffect(() => {
		const closeMenu = () => setOpenMenu(null);
		window.addEventListener("resize", closeMenu);
		window.addEventListener("scroll", closeMenu, true);
		return () => {
			window.removeEventListener("resize", closeMenu);
			window.removeEventListener("scroll", closeMenu, true);
		};
	}, []);

	useEffect(() => {
		if (files.length === 0) {
			setActiveDocumentId(null);
			return;
		}

		if (!activeDocumentId || !files.some((file) => file.id === activeDocumentId)) {
			setActiveDocumentId(files[0].id);
		}
	}, [files, activeDocumentId]);

	const handleCheckWorkOrder = async (file: FileResponse) => {
		setOpenMenu(null);
		setActiveDocumentId(file.id);
		setCheckError(null);
		setCheckLoading(file.id);

		try {
			const result = await checkWorkOrder(file.id);
			// Повторная проверка: новые результаты заменяют предыдущие
			setCheckResults((prev) => ({ ...prev, [file.id]: result }));
		} catch (err) {
			setCheckError(err instanceof Error ? err.message : "Ошибка проверки");
		} finally {
			setCheckLoading(null);
		}
	};

	return (
		<div className="space-y-4">
			<PageHeader
				title="Проверка нарядов"
				subtitle="Список загруженных нарядов. Для проверки наряда выберите его и нажмите «Проверить наряд»."
			/>

			{isLoading ? (
				<LoadingState message="Загрузка файлов..." />
			) : files.length === 0 ? (
				<EmptyState title="Нет загруженных нарядов." />
			) : (
				<div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
					<table className="w-full table-fixed text-sm">
						{/* Фича 038: имя файла занимает оставшуюся ширину, остальные колонки фиксированы */}
						<colgroup>
							<col />
							<col className="w-28" />
							<col className="w-20" />
							<col className="w-20" />
							<col className="w-48" />
							<col className="w-36" />
							<col className="w-24" />
						</colgroup>
						<thead className="border-b border-slate-200 bg-slate-50">
							<tr>
								<th className="px-3 py-2.5 text-left font-medium text-slate-600">Файл</th>
								<th className="px-3 py-2.5 text-left font-medium text-slate-600">Лист</th>
								<th className="px-3 py-2.5 text-left font-medium text-slate-600">Тип</th>
								<th className="px-3 py-2.5 text-left font-medium text-slate-600">Период</th>
								<th className="px-3 py-2.5 text-left font-medium text-slate-600">Объект</th>
								<th className="px-3 py-2.5 text-left font-medium text-slate-600">Дата загрузки</th>
								<th className="px-3 py-2.5 text-left font-medium text-slate-600">Действия</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{files.map((file) => (
								<tr
									key={file.id}
									onClick={() => {
										setActiveDocumentId(file.id);
										setCheckError(null);
									}}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											setActiveDocumentId(file.id);
											setCheckError(null);
										}
									}}
									tabIndex={0}
									className={`cursor-pointer transition-colors focus:outline-none focus:ring-1 focus:ring-blue-300 ${
										activeDocumentId === file.id ? "bg-slate-50" : "hover:bg-slate-50"
									}`}
								>
									<td className="px-3 py-2 text-slate-700">
										<TruncatedText value={file.originalName} className="font-medium" />
									</td>
									<td className="px-3 py-2 text-slate-700">
										{file.sheetName ? (
											<TruncatedText value={file.sheetName} />
										) : (
											"—"
										)}
									</td>
									<td className="px-3 py-2 text-slate-700">
										<span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
											{fileTypeLabels[file.fileType]}
										</span>
									</td>
									<td className="px-3 py-2 text-slate-700">
										{formatPeriod(file.periodMonth, file.periodYear)}
									</td>
									<td className="px-3 py-2 text-slate-700">
										{file.objectName ? (
											<TruncatedText value={file.objectName} className="max-w-xs" />
										) : (
											"—"
										)}
									</td>
									<td className="whitespace-nowrap px-3 py-2 text-slate-700">
										{formatUploadDate(file.createdAt)}
									</td>
									<td className="px-3 py-2 text-slate-700">
										{file.fileType === "work_order" ? (
											<div className="inline-block">
												<button
													type="button"
													onClick={(event) => {
														event.stopPropagation();
														const menuWidth = 220;
														const viewportPadding = 8;
														const estimatedMenuHeight = 96;
														const buttonRect = (
															event.currentTarget as HTMLButtonElement
														).getBoundingClientRect();

														let left = buttonRect.right - menuWidth;
														if (left < viewportPadding) {
															left = viewportPadding;
														}

														let top = buttonRect.bottom + 6;
														if (top + estimatedMenuHeight > window.innerHeight - viewportPadding) {
															top = Math.max(
																viewportPadding,
																buttonRect.top - estimatedMenuHeight - 6,
															);
														}

														setOpenMenu((prev) =>
															prev?.fileId === file.id ? null : { fileId: file.id, left, top },
														);
													}}
													data-row-menu-root="true"
													className="inline-flex items-center rounded-md border border-slate-300 px-2 py-1 text-slate-600 transition-colors hover:bg-slate-100"
													aria-label={`Открыть меню для файла ${formatFileSheetLabel(file.originalName, file.sheetName, uiMessages.file_sheet_separator)}`}
												>
													<MoreHorizontal className="h-4 w-4" />
												</button>
											</div>
										) : (
											<span className="text-slate-400">—</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{openMenu && (
				<div
					data-row-menu-root="true"
					className="fixed z-50 w-[220px] rounded-md border border-slate-200 bg-white p-1 shadow-lg"
					style={{ left: openMenu.left, top: openMenu.top }}
				>
					<button
						type="button"
						onClick={() => {
							const file = files.find((entry) => entry.id === openMenu.fileId);
							if (!file) return;
							handleCheckWorkOrder(file);
						}}
						className="block w-full rounded px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100"
					>
						Проверить наряд
					</button>
				</div>
			)}

			<div className="rounded-lg border border-slate-200 bg-white p-4">
				<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
					<div>
						<h2 className="text-base font-semibold text-slate-900">Результаты проверки</h2>
						<p className="text-sm text-slate-500">
							{activeDocument
								? `Активный документ: ${formatFileSheetLabel(activeDocument.originalName, activeDocument.sheetName, uiMessages.file_sheet_separator)}`
								: "Активный документ не выбран"}
						</p>
					</div>
				</div>

				{/* Loading state */}
				{checkLoading && checkLoading === activeDocumentId && (
					<div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
						<Loader2 className="h-4 w-4 animate-spin" />
						Выполняется проверка наряда...
					</div>
				)}

				{/* Error state */}
				{checkError && !checkLoading && (
					<div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
						<XCircle className="h-4 w-4" />
						{checkError}
					</div>
				)}

				{/* Results */}
				{activeResult && !checkLoading ? (
					<div className="space-y-3">
						{/* Summary */}
						<div className="flex flex-wrap gap-3">
							<SummaryBadge
								label="Строк"
								value={activeResult.totalLines}
								color="gray"
								active={activeLineFilter === "all"}
								onClick={() => setActiveLineFilter((prev) => (prev === "all" ? null : "all"))}
							/>
							<SummaryBadge
								label="Прошло"
								value={activeResult.passedCount}
								color="green"
								active={activeLineFilter === "passed"}
								onClick={() => setActiveLineFilter((prev) => (prev === "passed" ? null : "passed"))}
							/>
							<SummaryBadge
								label="Ошибок"
								value={activeResult.errorCount}
								color="red"
								active={activeLineFilter === "error"}
								onClick={() => setActiveLineFilter((prev) => (prev === "error" ? null : "error"))}
							/>
							<SummaryBadge
								label="Предупреждений"
								value={activeResult.warningCount}
								color="yellow"
								active={activeLineFilter === "warning"}
								onClick={() =>
									setActiveLineFilter((prev) => (prev === "warning" ? null : "warning"))
								}
							/>
						</div>

						{/* Line results */}
						{activeResult.lines.length > 0 && (
							<div className="overflow-x-auto rounded-md border border-slate-200">
								<table className="w-full text-sm">
									<thead className="border-b border-slate-200 bg-slate-50">
										<tr>
											<th className="px-3 py-2 text-left font-medium text-slate-600 w-12">#</th>
											<th className="px-3 py-2 text-left font-medium text-slate-600 w-24">Код</th>
											<th className="px-3 py-2 text-left font-medium text-slate-600">
												Наименование
											</th>
											<th className="px-3 py-2 text-left font-medium text-slate-600 w-20">
												Ед. изм.
											</th>
											<th className="px-3 py-2 text-right font-medium text-slate-600 w-24">
												Кол-во
											</th>
											<th className="px-3 py-2 text-right font-medium text-slate-600 w-24">
												Расценка
											</th>
											<th className="px-3 py-2 text-right font-medium text-slate-600 w-16">К</th>
											<th className="px-3 py-2 text-right font-medium text-slate-600 w-24">
												Сумма
											</th>
											<th className="px-3 py-2 text-left font-medium text-slate-600 w-20">
												Статус
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
										{filteredLines.length === 0 ? (
											<tr>
												<td colSpan={9} className="px-3 py-8 text-center text-slate-500">
													По выбранному фильтру строки не найдены
												</td>
											</tr>
										) : (
											filteredLines.map((line) => (
												<LineResultRow
													key={line.rowNumber}
													line={line}
													fileId={activeDocument!.id}
													onRecheck={() => handleCheckWorkOrder(activeDocument!)}
												/>
											))
										)}
									</tbody>
								</table>
							</div>
						)}
					</div>
				) : !checkLoading && !checkError ? (
					activeDocument ? (
						<div className="rounded-md border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
							Для активного документа пока нет результатов проверки. Нажмите «Проверить наряд» в
							меню действий.
						</div>
					) : (
						<div className="rounded-md border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
							Выберите документ в списке выше, чтобы увидеть результаты проверки.
						</div>
					)
				) : null}
			</div>
		</div>
	);
}

function SummaryBadge({
	label,
	value,
	color,
	active,
	onClick,
}: {
	label: string;
	value: number;
	color: "gray" | "green" | "red" | "yellow";
	active: boolean;
	onClick: () => void;
}) {
	const colorClasses = {
		gray: "bg-slate-100 text-slate-700",
		green: "bg-green-100 text-green-700",
		red: "bg-red-100 text-red-700",
		yellow: "bg-yellow-100 text-yellow-700",
	};

	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-1 ${
				colorClasses[color]
			} ${active ? "ring-2 ring-offset-1 ring-slate-400" : "hover:opacity-90"}`}
		>
			{label}: {value}
		</button>
	);
}

function lineHasError(line: CheckWorkOrderResponse["lines"][number]): boolean {
	return line.violations.some((violation) => violation.severity === "error");
}

function LineResultRow({
	line,
	fileId,
	onRecheck,
}: {
	line: CheckWorkOrderResponse["lines"][number];
	fileId: number;
	onRecheck: () => Promise<void>;
}) {
	const [expanded, setExpanded] = useState(false);
	const [addingSynonym, setAddingSynonym] = useState<string | null>(null);

	const handleAddSynonym = async (canonicalUnit: string, synonym: string, violationKey: string) => {
		setAddingSynonym(violationKey);
		try {
			await addUnitSynonym(canonicalUnit, synonym);
		} catch (error) {
			// Если синоним уже существует — не показываем ошибку, просто перезапустим проверку
			const msg = error instanceof Error ? error.message : "";
			if (!msg.includes("уже существует")) {
				console.error("Failed to add synonym:", error);
				alert(msg || "Ошибка добавления синонима");
				setAddingSynonym(null);
				return;
			}
		}
		try {
			await onRecheck();
		} finally {
			setAddingSynonym(null);
		}
	};

	return (
		<>
			<tr
				onClick={() => !line.passed && setExpanded((prev) => !prev)}
				onKeyDown={(e) => {
					if (!line.passed && (e.key === "Enter" || e.key === " ")) {
						e.preventDefault();
						setExpanded((prev) => !prev);
					}
				}}
				tabIndex={!line.passed ? 0 : undefined}
				className={`${!line.passed ? "cursor-pointer hover:bg-slate-50" : ""}`}
			>
				<td className="px-3 py-2 text-slate-500">{line.rowNumber}</td>
				<td className="px-3 py-2 text-slate-700 font-mono text-xs">{line.rateNumber ?? "—"}</td>
				<td className="px-3 py-2 text-slate-700 truncate max-w-[300px]" title={line.workName}>
					{line.workName}
				</td>
				<td className="px-3 py-2 text-slate-700">{line.unit ?? "—"}</td>
				<td className="px-3 py-2 text-right text-slate-700 font-mono">
					{formatDecimal(line.quantity)}
				</td>
				<td className="px-3 py-2 text-right text-slate-700 font-mono">
					{formatInteger(line.rate)}
				</td>
				<td className="px-3 py-2 text-right text-slate-700 font-mono">
					{line.k === null || line.k === undefined ? "" : formatDecimal(line.k)}
				</td>
				<td className="px-3 py-2 text-right text-slate-700 font-mono">
					{formatInteger(line.amount)}
				</td>
				<td className="px-3 py-2">
					{line.passed ? (
						<CheckCircle2 className="h-4 w-4 text-green-500" />
					) : line.violations.some((v) => v.severity === "error") ? (
						<XCircle className="h-4 w-4 text-red-500" />
					) : (
						<AlertTriangle className="h-4 w-4 text-yellow-500" />
					)}
				</td>
			</tr>
			{expanded && !line.passed && (
				<tr>
					<td colSpan={9} className="px-3 py-2 bg-slate-50">
						<div className="space-y-1.5">
							{line.violations.map((v, i) => (
								<div
									key={`${v.ruleId}-${i}`}
									className={`rounded px-3 py-2 text-xs ${
										v.severity === "error"
											? "border border-red-200 bg-red-50 text-red-800"
											: "border border-yellow-200 bg-yellow-50 text-yellow-800"
									}`}
								>
									<div className="flex items-center gap-2">
										<span className="font-medium">
											{v.ruleLabel}: {v.ruleName}
										</span>
										<span
											className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
												v.severity === "error"
													? "bg-red-200 text-red-800"
													: "bg-yellow-200 text-yellow-800"
											}`}
										>
											{v.severity}
										</span>
									</div>
									<p className="mt-1">{v.message}</p>
									{(v.expected || v.actual) && (
										<div className="mt-1 flex gap-3 text-[11px]">
											{v.expected && (
												<span>
													Ожидалось: <span className="font-mono">{v.expected}</span>
												</span>
											)}
											{v.actual && (
												<span>
													Фактически: <span className="font-mono">{v.actual}</span>
												</span>
											)}
										</div>
									)}
									{v.canAddSynonym && v.metadata?.canonicalUnit && v.actual && (
										<div className="mt-2">
											<button
												type="button"
												onClick={() =>
													handleAddSynonym(
														v.metadata!.canonicalUnit as string,
														v.actual as string,
														`${v.ruleId}-${i}`,
													)
												}
												disabled={addingSynonym === `${v.ruleId}-${i}`}
												className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition ${
													addingSynonym === `${v.ruleId}-${i}`
														? "cursor-not-allowed opacity-50"
														: v.severity === "error"
															? "bg-red-200 text-red-900 hover:bg-red-300"
															: "bg-yellow-200 text-yellow-900 hover:bg-yellow-300"
												}`}
											>
												{addingSynonym === `${v.ruleId}-${i}` ? (
													<Loader2 className="h-3 w-3 animate-spin" />
												) : (
													<Plus className="h-3 w-3" />
												)}
												{addingSynonym === `${v.ruleId}-${i}`
													? "Добавление..."
													: "Добавить в словарь синонимов"}
											</button>
										</div>
									)}
								</div>
							))}
						</div>
					</td>
				</tr>
			)}
		</>
	);
}
